<template>
  <sera-modal
    open
    type="info"
    size="600"
    :modalTitle="modalTitle"
    primaryLabel="Save"
    @primary-action="handleSave"
    @close="emit('hide-modal')"
  >
    <Spinner v-if="loading" message="Loading Note details..." />
    <div v-else>
      <div class="mb-3">
        <sera-checkbox
          :checked="markAsImportant"
          data-cy="mark-as-important"
          class="mb-2"
          @change="({ detail }) => (markAsImportant = detail)"
        >
          <div class="sera-body-small">Pin</div>
        </sera-checkbox>
        <sera-checkbox
          v-if="canChangeCustomerVisibility"
          data-cy="mark-visible-to-customer"
          :checked="visibleToCustomer"
          @change="({ detail }) => (visibleToCustomer = detail)"
        >
          <div class="sera-body-small">Mark Visible to Customer</div>
        </sera-checkbox>
      </div>
    </div>

    <div class="d-flex full-width align-items-baseline">
      <sera-input
        :value="comment"
        multiline
        label="Note"
        height="100px"
        class="flex-1"
        placeholder="Add note..."
        dataCy="comment-form-body-textarea"
        required
        :errorMessage="firstErrorMessage(v$.comment)"
        @change="({ detail }) => (comment = detail)"
      >
      </sera-input>
    </div>
  </sera-modal>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import API from "serviceshift-ui/api-client";
import { computed, onMounted, ref } from "vue";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";
import { useStore } from "vuex";

import Spinner from "@/components/Spinner2.vue";
import { Comment } from "@/typings/comment";
import { Customer } from "@/typings/customer";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";

const props = defineProps({
  commentableType: {
    type: String,
    default: ""
  },
  commentableId: {
    type: Number,
    default: null
  },
  comment: {
    type: Object as () => Comment,
    default: null
  }
});
const emit = defineEmits(["hide-modal", "updated", "created"]);

const store = useStore();
const customer = ref<Customer>();
const comment = ref("");
const loading = ref(true);
const markAsImportant = ref(false);
const visibleToCustomer = ref(false);
const canChangeCustomerVisibility = ref(true);
const v$ = useVuelidate(
  {
    comment: {
      required: helpers.withMessage("Note is required", required)
    }
  },
  {
    comment
  }
);

const actualCommentableType = computed(() => {
  return props.comment
    ? props.comment?.commentable_type
    : props.commentableType;
});

const actualCommentableId = computed(() => {
  return props.comment ? props.comment?.commentable_id : props.commentableId;
});

const modalTitle = computed(() => {
  if (props.comment) return "Edit Note";
  return "New Note";
});

onMounted(async () => {
  let customerId;
  if (actualCommentableType.value === "User") {
    customerId = actualCommentableId.value;
  }
  if (props.comment) {
    comment.value = props.comment.comment;
    markAsImportant.value = props.comment.pinned;
    visibleToCustomer.value = props.comment.customer_visible;
    canChangeCustomerVisibility.value = props.comment.customer_visible_editable;
    if (props.comment.commentable_type === "User") {
      customerId = props.comment.commentable_id;
    }
  }
  if (customerId) {
    await fetchCustomer(customerId);
  }
  loading.value = false;
});

async function fetchCustomer(customerId) {
  try {
    const response = await store.dispatch("fetchUser", {
      id: customerId,
      returnOnly: true
    });
    customer.value = response.data;
  } catch (e) {
    emit("hide-modal");
    throw e;
  }
}

function handleSave() {
  if (props.comment) return updateComment();
  return createComment();
}

const updateComment = debounce(
  async () => {
    const data = await getData();
    await API.makeRequest(`/v1/comments/${props.comment.id}`, {
      method: "PUT",
      data: {
        comment: data
      }
    });
    emit("updated");
  },
  700,
  { leading: true }
);

const createComment = debounce(
  async () => {
    const data = await getData();
    await API.makeRequest(`/v1/comments`, {
      method: "POST",
      data: {
        comment: data
      }
    });
    emit("created");
  },
  700,
  { leading: true }
);

async function getData() {
  const res = await v$.value.$validate();
  if (!res) throw res;
  let commentable_type = actualCommentableType.value;
  let commentable_id = actualCommentableId.value;

  // You are allowed to change the address on a User type of comment
  if (["User"].includes(commentable_type)) {
    commentable_type = "User";
    commentable_id = customer.value!.id;
  }

  return {
    commentable_id,
    commentable_type,
    comment: comment.value,
    pinned: markAsImportant.value,
    customer_visible: visibleToCustomer.value
  };
}
</script>

<style lang="scss">
.customer-address-select {
  width: 350px;
}
</style>
